/**
 * SEO Helper Functions
 *
 * Utilities for generating SEO-friendly URLs, metadata, and structured data
 * for video content to improve search engine visibility.
 */

/**
 * Generate a URL-safe slug from video title and location
 * @param {string} title - Video title
 * @param {string} location - Video location (city/state)
 * @param {string} videoId - Original video ID (fallback)
 * @returns {string} URL-safe slug (max ~60 chars for SERP display)
 */
export const generateVideoSlug = (title, location, videoId) => {
  // Combine title and location for maximum SEO value
  const slugBase = `${title || 'video'}-${location || ''}`;

  // Convert to lowercase and replace spaces/special chars with hyphens
  let slug = slugBase
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
    .replace(/\s+/g, '-')      // Replace spaces with hyphens
    .replace(/-+/g, '-')       // Replace multiple hyphens with single
    .replace(/^-+|-+$/g, '');  // Remove leading/trailing hyphens

  // If slug is too short or empty, use video ID as fallback
  if (slug.length < 3) {
    return videoId;
  }

  // Use last 8 chars of ID as short suffix (collision-resistant for reasonable video counts)
  const idSuffix = videoId.slice(-8);

  // Limit slug to ~50 chars before adding 8-char suffix (total ~60 chars)
  if (slug.length > 50) {
    slug = slug.substring(0, 50).replace(/-+$/, ''); // Remove trailing hyphens
  }

  // Append short suffix for uniqueness without leaking full ID
  return `${slug}-${idSuffix}`;
};

/**
 * Parse slug to extract video ID suffix
 * @param {string} slug - URL slug
 * @returns {string} Video ID suffix (last 8 chars) or full ID
 */
export const parseSlugToVideoId = (slug) => {
  // If slug contains hyphens, last segment should be the video ID suffix
  const parts = slug.split('-');
  const lastPart = parts[parts.length - 1];

  // Check if it's a full MongoDB ObjectId (24 hex chars) - backward compatibility
  if (lastPart && /^[a-f0-9]{24}$/i.test(lastPart)) {
    return lastPart;
  }

  // Check if it's an 8-char suffix (new format)
  if (lastPart && /^[a-f0-9]{8}$/i.test(lastPart)) {
    return lastPart; // Backend will look up by suffix
  }

  // Fallback: generic alphanumeric ID format (8+ chars)
  if (lastPart && /^[a-zA-Z0-9]{8,}$/.test(lastPart)) {
    return lastPart;
  }

  // Otherwise, assume entire slug is the video ID (backward compatibility)
  return slug;
};

/**
 * Generate SEO-optimized page title
 * @param {Object} video - Video object
 * @returns {string} Page title
 */
export const generateVideoTitle = (video) => {
  if (!video) return 'Video - ClipKite';

  const title = video.title || 'Untitled Video';
  const location = video.location || video.state || '';
  const site = 'ClipKite';

  if (location) {
    return `${title} - ${location} | ${site}`;
  }

  return `${title} | ${site}`;
};

/**
 * Generate SEO-optimized meta description
 * @param {Object} video - Video object
 * @returns {string} Meta description (max 160 chars)
 */
export const generateVideoDescription = (video) => {
  if (!video) return 'Watch and share video clips on ClipKite';

  const title = video.title || 'video';
  const location = video.location || video.state || '';
  const username = video.username || 'a user';
  const views = video.views || 0;

  // Create compelling description under 160 characters
  let description = `Watch ${title}`;

  if (location) {
    description += ` from ${location}`;
  }

  description += ` by ${username}`;

  if (views > 0) {
    description += `. ${views.toLocaleString()} views`;
  }

  description += ' on ClipKite.';

  // Truncate if too long
  if (description.length > 160) {
    description = description.substring(0, 157) + '...';
  }

  return description;
};

/**
 * Generate JSON-LD VideoObject structured data
 * @param {Object} video - Video object
 * @param {string} url - Canonical URL of the video page
 * @returns {Object} JSON-LD structured data
 */
export const generateVideoSchema = (video, url) => {
  if (!video) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title || 'Untitled Video',
    description: video.description || generateVideoDescription(video),
    thumbnailUrl: video.thumbnailUrl || video.posterUrl || `https://image.mux.com/${video.muxPlaybackId}/thumbnail.jpg`,
    uploadDate: video.createdAt || new Date().toISOString(),
    contentUrl: url,
    embedUrl: url,
    duration: video.duration ? `PT${Math.floor(video.duration)}S` : undefined,
  };

  // Add uploader information if available
  if (video.username) {
    schema.author = {
      '@type': 'Person',
      name: video.username,
    };
  }

  // Add interaction statistics
  if (video.views || video.likeCount) {
    schema.interactionStatistic = [];

    if (video.views) {
      schema.interactionStatistic.push({
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/WatchAction',
        userInteractionCount: video.views,
      });
    }

    if (video.likeCount) {
      schema.interactionStatistic.push({
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/LikeAction',
        userInteractionCount: video.likeCount,
      });
    }
  }

  // Add location information
  if (video.location || video.state) {
    schema.contentLocation = {
      '@type': 'Place',
      name: video.location || video.state,
    };
  }

  // Remove undefined fields
  Object.keys(schema).forEach(key => {
    if (schema[key] === undefined) {
      delete schema[key];
    }
  });

  return schema;
};

/**
 * Generate canonical URL for video
 * @param {string} slug - Video slug
 * @returns {string} Canonical URL
 */
export const generateCanonicalUrl = (slug) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/clips/${slug}`;
};

/**
 * Update document head with SEO metadata
 * @param {Object} video - Video object
 * @param {string} slug - Video slug
 */
export const updateVideoSEO = (video, slug) => {
  if (!video || !slug) return;

  // Update title
  document.title = generateVideoTitle(video);

  // Update or create meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    document.head.appendChild(metaDescription);
  }
  metaDescription.content = generateVideoDescription(video);

  // Update or create canonical link
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = generateCanonicalUrl(slug);

  // Update or create Open Graph tags
  const ogTags = [
    { property: 'og:title', content: video.title || 'Untitled Video' },
    { property: 'og:description', content: generateVideoDescription(video) },
    { property: 'og:type', content: 'video.other' },
    { property: 'og:url', content: generateCanonicalUrl(slug) },
    { property: 'og:image', content: video.thumbnailUrl || video.posterUrl || `https://image.mux.com/${video.muxPlaybackId}/thumbnail.jpg` },
    { property: 'og:video', content: `https://stream.mux.com/${video.muxPlaybackId}.m3u8` },
    { property: 'og:site_name', content: 'ClipKite' },
  ];

  ogTags.forEach(({ property, content }) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('property', property);
      document.head.appendChild(tag);
    }
    tag.content = content;
  });

  // Update or create Twitter Card tags
  const twitterTags = [
    { name: 'twitter:card', content: 'player' },
    { name: 'twitter:title', content: video.title || 'Untitled Video' },
    { name: 'twitter:description', content: generateVideoDescription(video) },
    { name: 'twitter:image', content: video.thumbnailUrl || video.posterUrl || `https://image.mux.com/${video.muxPlaybackId}/thumbnail.jpg` },
    { name: 'twitter:player', content: generateCanonicalUrl(slug) },
  ];

  twitterTags.forEach(({ name, content }) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.name = name;
      document.head.appendChild(tag);
    }
    tag.content = content;
  });

  // Add or update JSON-LD structured data
  let scriptTag = document.querySelector('script[type="application/ld+json"]');
  if (!scriptTag) {
    scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    document.head.appendChild(scriptTag);
  }

  const schema = generateVideoSchema(video, generateCanonicalUrl(slug));
  scriptTag.textContent = JSON.stringify(schema, null, 2);
};
